import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import Input from "~/components/formElements/Input";
import SubmitButton from "~/components/formElements/SubmitButton";
import Headline from "~/components/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	updateStaffMembersSocialSecurityAndPensionInsuranceDetails,
} from "~/modules/humanResources/api/staffMember/staffMemberApiDispatchers.ts";
import type { StaffMember } from "~/modules/humanResources/api/staffMember/staffMemberTypes.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type UpdateSocialSecurityAndTaxDetailsFormData = {
	socialSecurityNumber: string | null;
	pensionInsuranceNumber: string | null;
}

type Props = {
	onSuccess: () => void;
	onCancel: () => void;
	staffMemberCoreData: StaffMember;
};

const UpdateSocialSecurityAndPensionInsuranceDetailsForm: React.FunctionComponent<Props> = ({
	onSuccess,
	onCancel,
	staffMemberCoreData,
}) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const schema = useMemo(() => {
		return yup.object({
			socialSecurityNumber: yup.string().nullable().default(null),
			pensionInsuranceNumber: yup.string().nullable().default(null),
		});
	}, []);

	const defaultValues = useMemo(() => {
		if (staffMemberCoreData) {
			return {
				socialSecurityNumber: staffMemberCoreData.socialSecurityNumber,
				pensionInsuranceNumber: staffMemberCoreData.pensionInsuranceNumber,
			};
		}
		return {
			socialSecurityNumber: null,
			pensionInsuranceNumber: null,

		};
	}, [staffMemberCoreData]);

	const {
		handleSubmit,
		control,
		formState: { isDirty, isSubmitted, isValid },
	} = useForm<UpdateSocialSecurityAndTaxDetailsFormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<UpdateSocialSecurityAndTaxDetailsFormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isSubmitted,
		isDirty,
		isValid,
		isLoading: isBusy,
	});

	const onSubmit = useCallback(async (data: UpdateSocialSecurityAndTaxDetailsFormData) => {

		try {
			setIsBusy(true);
			await updateStaffMembersSocialSecurityAndPensionInsuranceDetails(staffMemberCoreData.id, data);
			onSuccess();
		} catch (error) {
			const apiError = handleApiError(error);
			console.log(apiError);
			setServerErrorMessage("Ein unerwarteter Fehler ist aufgetreten.");
		}
	}, [onSuccess, staffMemberCoreData.id]);

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<Headline type="h4"
						  color="muted">
					Sozial- und Rentenversicherungsnummer
				</Headline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}

				<Input control={control}
					   name="socialSecurityNumber"
					   label="Sozialversicherungsnummer"
					   placeholder="1234567890"
				/>
				<div className="h-4"/>
				<Input control={control}
					   name="pensionInsuranceNumber"
					   label="Rentenversicherunsnummer"
					   placeholder="DE1234567890"
				/>

			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Speichern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default UpdateSocialSecurityAndPensionInsuranceDetailsForm;
